import React, { createContext, useContext, useEffect, useState } from "react";
import { authRegister, authLogin, authMe, authUpdateProfile, authChangeEmail, authChangePassword } from "../lib/api";

// Public website visitor accounts — backed by the FastAPI + MongoDB backend (JWT).
const UserContext = createContext(null);
const TOKEN_KEY = "rfl_user_token";

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(() => !!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    authMe(token)
      .then(setUser)
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const persist = (tk, u) => {
    localStorage.setItem(TOKEN_KEY, tk);
    setToken(tk);
    setUser(u);
  };

  const signup = async ({ name, email, password, phone }) => {
    try {
      const data = await authRegister({ name, email, password, phone });
      persist(data.token, data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Could not create account." };
    }
  };

  const login = async ({ email, password }) => {
    try {
      const data = await authLogin({ email, password });
      if (data.user.role === "admin" || data.user.role === "consultant") {
        // Staff belong in the back office, not the client portal.
        return { ok: true, admin: true, token: data.token, user: data.user };
      }
      persist(data.token, data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Invalid email or password." };
    }
  };

  const updateProfile = async (payload) => {
    try {
      const u = await authUpdateProfile(token, payload);
      setUser(u);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: "Could not save your profile." };
    }
  };

  const changeEmail = async ({ email, currentPassword }) => {
    try {
      const u = await authChangeEmail(token, email, currentPassword);
      setUser(u);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Could not change your email." };
    }
  };

  const changePassword = async ({ currentPassword, newPassword }) => {
    try {
      await authChangePassword(token, currentPassword, newPassword);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Could not change your password." };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  return (
    <UserContext.Provider value={{ user, token, loading, signup, login, logout, updateProfile, changeEmail, changePassword }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
